// @mui
import MUIGlobalStyles from '@mui/material/GlobalStyles'
import { alpha, type Theme } from '@mui/material/styles'

// ----------------------------------------------------------------------

/**
 * The bridge the legacy SASS layer reads the theme through.
 *
 * `client/app/src/sass/**` predates the theme and knows nothing about colour modes:
 * it paints `#fff`, `$pageBackground` and `$borderGrey` straight into the
 * stylesheet, so every surface it owns stayed light while the MUI half of the same
 * page went dark — and the themed ink those surfaces inherit landed on them at
 * 1.15:1.
 *
 * Custom properties are the only bridge that works: SASS colour functions
 * (`darken($lightGrey, 5%)`, used in ~230 places) cannot take a `var()` as input, so
 * the variables themselves have to stay literal. Declarations move over one at a
 * time as `var(--tm-surface, #fff)`, where the fallback is the value that
 * declaration already had.
 *
 * These are published in dark mode ONLY, and that is the point: in light mode every
 * bridged declaration falls through to its original literal, so converting one
 * cannot change the light rendering by a single byte. `--tm-switch-off` is the one
 * exception — the legacy switch drew its *off* state in `tomato`, which is a bug in
 * both modes.
 */
const legacyBridge = (theme: Theme) => ({
  ':root': {
    // Matches MuiSwitch's own off-track, so the two switch implementations stop
    // disagreeing about what "off" looks like.
    '--tm-switch-off': alpha(theme.palette.grey[500], 0.48),
    ...(theme.palette.mode === 'dark' && {
      '--tm-bg': theme.palette.background.default,
      '--tm-surface': theme.palette.background.paper,
      '--tm-surface-2': theme.palette.background.neutral,
      '--tm-ink': theme.palette.text.primary,
      '--tm-ink-2': theme.palette.text.secondary,
      '--tm-ink-3': theme.palette.text.tertiary,
      '--tm-accent-ink': theme.palette.primary.light,
      '--tm-line': theme.palette.divider,
      '--tm-hover': theme.palette.action.hover,
      '--tm-disabled-bg': theme.palette.action.disabledBackground,
    }),
  },
})

export default function GlobalStyles() {
  const inputGlobalStyles = (
    <MUIGlobalStyles
      styles={(theme: Theme) => ({
        ...legacyBridge(theme),
        '*': {
          boxSizing: 'border-box',
        },
        html: {
          margin: 0,
          padding: 0,
          width: '100%',
          height: '100%',
          WebkitOverflowScrolling: 'touch',
        },
        body: {
          margin: 0,
          padding: 0,
          width: '100%',
          height: '100%',
        },
        '#root': {
          width: '100%',
          height: '100%',
        },
        input: {
          '&[type=number]': {
            MozAppearance: 'textfield',
            '&::-webkit-outer-spin-button': {
              margin: 0,
              WebkitAppearance: 'none',
            },
            '&::-webkit-inner-spin-button': {
              margin: 0,
              WebkitAppearance: 'none',
            },
          },
        },
        img: {
          display: 'block',
          maxWidth: '100%',
        },
        ul: {
          margin: 0,
          padding: 0,
        },
        // MUI does not honor prefers-reduced-motion by itself. Near-zero
        // instead of `none` so transitionend/animationend still fire —
        // Snackbar auto-hide and Dialog unmount depend on them.
        '@media (prefers-reduced-motion: reduce)': {
          '*, *::before, *::after': {
            transitionDuration: '0.01ms !important',
            animationDuration: '0.01ms !important',
            animationIterationCount: '1 !important',
            scrollBehavior: 'auto !important',
          },
        },
      })}
    />
  )

  return inputGlobalStyles
}
